import { useEffect, useState, type FormEvent } from "react";
import type { AdminPublicSettingsDto } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminPublicSettings, patchAdminPublicSettings } from "../../shared/api/public";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const pageLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/public-events", label: "公開イベント" }
];

export function AdminPublicSettingsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [settings, setSettings] = useState<AdminPublicSettingsDto | null>(null);
  const [showPublicSchedule, setShowPublicSchedule] = useState(false);
  const [showPublicEvents, setShowPublicEvents] = useState(false);
  const [publicScheduleWindowDays, setPublicScheduleWindowDays] = useState("60");
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [saveState, setSaveState] = useState<"idle" | "saving">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    let cancelled = false;

    async function loadSettings() {
      setLoadState("loading");
      setMessage(null);

      try {
        const nextSettings = await fetchAdminPublicSettings(tenantSlug);

        if (cancelled) {
          return;
        }

        setSettings(nextSettings);
        setShowPublicSchedule(nextSettings.showPublicSchedule);
        setShowPublicEvents(nextSettings.showPublicEvents);
        setPublicScheduleWindowDays(String(nextSettings.publicScheduleWindowDays));
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setMessage(getProtectedApiErrorMessage(error, "公開設定の取得に失敗しました"));
      }
    }

    void loadSettings();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaveState("saving");
    setMessage(null);

    try {
      const nextSettings = await patchAdminPublicSettings(tenantSlug, {
        showPublicSchedule,
        showPublicEvents,
        publicScheduleWindowDays: Number.parseInt(publicScheduleWindowDays, 10) || 60
      });
      setSettings(nextSettings);
      setShowPublicSchedule(nextSettings.showPublicSchedule);
      setShowPublicEvents(nextSettings.showPublicEvents);
      setPublicScheduleWindowDays(String(nextSettings.publicScheduleWindowDays));
      setMessage("公開設定を保存しました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setMessage(getProtectedApiErrorMessage(error, "公開設定の保存に失敗しました"));
    } finally {
      setSaveState("idle");
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="公開設定"
      description="TOPページに表示する公開導線を管理します。"
      links={pageLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p style={{ margin: 0 }}>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p style={{ margin: 0 }}>Entry へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p style={{ margin: 0 }}>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p style={{ margin: 0 }}>この画面は admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p style={{ margin: 0 }}>公開設定を読み込んでいます。</p> : null}
        {message ? <p style={{ margin: 0 }}>{message}</p> : null}

        {isSessionReady && hasAdminRole && settings ? (
          <form onSubmit={(event) => void handleSubmit(event)} style={{ display: "grid", gap: 16 }}>
            <label style={{ display: "grid", gap: 8 }}>
              <span style={{ fontWeight: 600 }}>公開表示</span>
              <span>
                <input
                  type="checkbox"
                  checked={showPublicSchedule}
                  onChange={(event) => setShowPublicSchedule(event.target.checked)}
                />
                {" "}TOPに公開スケジュールを表示
              </span>
              <span>
                <input
                  type="checkbox"
                  checked={showPublicEvents}
                  onChange={(event) => setShowPublicEvents(event.target.checked)}
                />
                {" "}TOPに公開イベントを表示
              </span>
            </label>

            <label style={{ display: "grid", gap: 8 }}>
              <span style={{ fontWeight: 600 }}>公開スケジュール期間（日）</span>
              <input
                type="number"
                min={1}
                value={publicScheduleWindowDays}
                onChange={(event) => setPublicScheduleWindowDays(event.target.value)}
                style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
              />
            </label>

            <div style={{ fontSize: 13, opacity: 0.8 }}>
              tenant timezone: {settings.timeZone}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="submit" disabled={saveState === "saving"}>
                {saveState === "saving" ? "保存中..." : "保存"}
              </button>
              <Link to={`/t/${tenantSlug}`}>公開TOPを確認</Link>
            </div>
          </form>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
